(() => {
if (customElements.get('collection-filter-nav')) return;

class CollectionFilterNav extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onFiltersUpdated = this.onFiltersUpdated.bind(this);
    this._initialized = false;
    this._facetWaitId = 0;
  }

  connectedCallback() {
    this.primaryKey = this.dataset.primaryKey || '';
    this.secondaryKey = this.dataset.secondaryKey || '';
    this.addEventListener('click', this.onClick);
    document.addEventListener('collection-filters:updated', this.onFiltersUpdated);
    window.addEventListener('popstate', this.onFiltersUpdated);
    this.placeSidebarDescription();
    // Sidebar copy is resolved in Liquid for first paint; keep JS in sync before facets load.
    this.syncSidebarDescription(
      this.getParamValue(this.primaryKey),
      this.getParamValue(this.secondaryKey)
    );
    this.initialize();
  }

  placeSidebarDescription() {
    const panel = document.getElementById('ComponentsCollectionSidebarDescription');
    const slot = document.querySelector('[data-collection-sidebar-description-slot]');
    if (!panel || !slot) return;
    // replaceChildren clears the grid's fallback node when filter-nav loads after
    // the product grid (inline relocation script never saw this panel).
    slot.replaceChildren(panel);
    panel.classList.remove('components-collection-sidebar__description--pending-slot');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('collection-filters:updated', this.onFiltersUpdated);
    window.removeEventListener('popstate', this.onFiltersUpdated);
    // Invalidate any in-flight facets wait so reconnect can re-initialize.
    this._facetWaitId += 1;
    window.clearTimeout(this._facetWaitTimeout);
    this._facetWaitTimeout = null;
  }

  initialize() {
    if (this._initialized) return;

    // Sync from the URL immediately; do not depend on facets.js for active states.
    this.sync();

    const waitId = ++this._facetWaitId;
    this.waitForFacets().then(() => {
      if (waitId !== this._facetWaitId || !this.isConnected) return;
      // Only mark initialized once facets are actually ready to patch.
      if (!this.facetsReady()) return;
      try {
        this.patchFacetRendering();
      } catch (error) {
        console.warn('collection-filter-nav: unable to patch facet URL updates.', error);
      }
      this._initialized = true;
      this.sync();
    });
  }

  facetsReady() {
    return (
      typeof FacetFiltersForm !== 'undefined' &&
      typeof FacetFiltersForm.renderPage === 'function' &&
      typeof FacetFiltersForm.updateURLHash === 'function'
    );
  }

  waitForFacets() {
    return new Promise((resolve) => {
      const waitId = this._facetWaitId;

      const wait = () => {
        if (waitId !== this._facetWaitId) {
          this._facetWaitTimeout = null;
          resolve();
          return;
        }

        if (this.facetsReady()) {
          this._facetWaitTimeout = null;
          resolve();
          return;
        }

        this._facetWaitTimeout = window.setTimeout(wait, 50);
      };

      wait();
    });
  }

  patchFacetRendering() {
    if (!this.facetsReady() || FacetFiltersForm._collectionFilterNavPatched) return;

    const notify = () => {
      document.dispatchEvent(
        new CustomEvent('collection-filters:updated', {
          detail: { searchParams: FacetFiltersForm.searchParamsPrev },
        })
      );
    };

    const originalUpdateURLHash = FacetFiltersForm.updateURLHash.bind(FacetFiltersForm);
    FacetFiltersForm.updateURLHash = function (searchParams) {
      originalUpdateURLHash(searchParams);
      notify();
    };
    FacetFiltersForm._collectionFilterNavPatched = true;
  }

  onFiltersUpdated() {
    this.sync();
  }

  onClick(event) {
    const tab = event.target.closest('[data-filter-action], [data-filter-key]');
    if (!tab || !this.contains(tab)) return;

    // Without a working FacetFiltersForm, allow the native filter link navigation.
    if (!this.facetsReady()) return;

    event.preventDefault();

    const params = new URLSearchParams(this.pendingSearchParams ?? window.location.search);

    if (tab.dataset.filterAction === 'clear-primary') {
      if (this.primaryKey) params.delete(this.primaryKey);
      if (this.secondaryKey) params.delete(this.secondaryKey);
    } else if (tab.dataset.filterAction === 'clear-secondary') {
      if (this.secondaryKey) params.delete(this.secondaryKey);
    } else if (tab.dataset.filterKey && tab.dataset.filterValue) {
      const key = tab.dataset.filterKey;
      const value = tab.dataset.filterValue;
      const isActive = params.get(key) === value;

      if (isActive) return;

      if (key === this.primaryKey && this.secondaryKey) {
        params.delete(this.secondaryKey);
      }

      params.set(key, value);
    }

    params.delete('page');
    const searchParams = params.toString();
    this.pendingSearchParams = searchParams;
    this.sync(params);

    const request = this.applyFilters(searchParams);
    if (request && typeof request.finally === 'function') {
      request.finally(() => {
        if (this.pendingSearchParams !== searchParams) return;
        this.pendingSearchParams = null;
        this.sync();
      });
    }
  }

  applyFilters(searchParams) {
    if (this.facetsReady() && document.getElementById('ProductGridContainer')) {
      try {
        return FacetFiltersForm.renderPage(searchParams);
      } catch (error) {
        console.warn('collection-filter-nav: facet render failed, falling back to navigation.', error);
      }
    }

    const query = searchParams ? `?${searchParams}` : '';
    window.location.href = `${window.location.pathname}${query}`;
  }

  getParamValue(key, params = new URLSearchParams(window.location.search)) {
    if (!key) return '';
    return params.get(key) || '';
  }

  getPrimaryTabLabel(primaryValue) {
    if (!primaryValue || !this.primaryKey) return '';

    const primaryTab = Array.from(this.querySelectorAll('[data-filter-key]')).find(
      (tab) =>
        tab.dataset.filterKey === this.primaryKey &&
        tab.dataset.filterValue === primaryValue &&
        !tab.dataset.parentValue
    );

    return primaryTab?.querySelector('.collection-filter-nav__tab-label')?.textContent?.trim() || '';
  }

  getFilterTab(key, value, parentValue) {
    return Array.from(this.querySelectorAll('[data-filter-key]')).find((tab) => {
      if (tab.dataset.filterKey !== key || tab.dataset.filterValue !== value) return false;
      // Secondary tabs are scoped to a primary; match Liquid's parent_value check.
      if (parentValue === undefined) return true;
      const parent = tab.dataset.parentValue;
      return !parent || parent === parentValue;
    });
  }

  /**
   * Match Liquid: a blank tab field is not an intentional clear — fall back to
   * the collection default for that field only.
   */
  resolveSidebarField(tabValue, fallback) {
    if (tabValue != null && String(tabValue).trim() !== '') return tabValue;
    return fallback ?? '';
  }

  tabHasSidebarCopy(tab) {
    return Boolean(
      tab &&
        (tab.hasAttribute('data-collection-title') || tab.hasAttribute('data-collection-description'))
    );
  }

  syncSidebarDescription(primaryValue, secondaryValue) {
    const activePrimaryTab = this.getFilterTab(this.primaryKey, primaryValue);
    const activeSecondaryTab = this.getFilterTab(this.secondaryKey, secondaryValue, primaryValue);
    // Prefer secondary, then primary — same order as the Liquid snippet.
    const descriptionTab = [activeSecondaryTab, activePrimaryTab].find((tab) => this.tabHasSidebarCopy(tab));
    const title = this.resolveSidebarField(
      descriptionTab?.dataset.collectionTitle,
      this.dataset.defaultCollectionTitle
    );
    const description = this.resolveSidebarField(
      descriptionTab?.dataset.collectionDescription,
      this.dataset.defaultCollectionDescription
    );

    document.querySelectorAll('[data-collection-sidebar-description]').forEach((container) => {
      const titleElement = container.querySelector('[data-collection-sidebar-title]');
      const descriptionElement = container.querySelector('[data-collection-sidebar-description-body]');

      const hasTitle = Boolean(title.trim());
      const hasDescription = Boolean(description.trim());

      if (titleElement) {
        titleElement.textContent = title;
        titleElement.hidden = !hasTitle;
      }
      if (descriptionElement) {
        descriptionElement.innerHTML = description;
        descriptionElement.hidden = !hasDescription;
      }
      container.hidden = !hasTitle && !hasDescription;
    });
  }

  sync(params = new URLSearchParams(window.location.search)) {
    const primaryValue = this.getParamValue(this.primaryKey, params);
    const secondaryValue = this.getParamValue(this.secondaryKey, params);

    this.querySelectorAll('[data-filter-action="clear-primary"]').forEach((tab) => {
      const isActive = !primaryValue;
      tab.classList.toggle('is-active', isActive);
      if (isActive) {
        tab.setAttribute('aria-current', 'page');
      } else {
        tab.removeAttribute('aria-current');
      }
    });

    this.querySelectorAll('[data-filter-key]').forEach((tab) => {
      const key = tab.dataset.filterKey;
      const value = tab.dataset.filterValue;
      const parent = tab.dataset.parentValue;
      const valueMatches = this.getParamValue(key, params) === value;
      // Secondary tabs share values across primaries (e.g. kits); only activate the scoped one.
      const isActive = valueMatches && (!parent || parent === primaryValue);

      tab.classList.toggle('is-active', isActive);
      if (isActive) {
        tab.setAttribute('aria-current', 'page');
      } else {
        tab.removeAttribute('aria-current');
      }
    });

    const primaryLabel = this.getPrimaryTabLabel(primaryValue);

    this.querySelectorAll('[data-filter-action="clear-secondary"]').forEach((tab) => {
      const isActive = Boolean(primaryValue) && !secondaryValue;
      tab.classList.toggle('is-active', isActive);
      if (isActive) {
        tab.setAttribute('aria-current', 'page');
      } else {
        tab.removeAttribute('aria-current');
      }

      const labelElement = tab.querySelector('.collection-filter-nav__tab-label');
      if (labelElement) {
        const baseLabel = tab.dataset.allSecondaryBase || 'All';
        labelElement.textContent = primaryLabel ? `${baseLabel} ${primaryLabel}` : baseLabel;
      }
    });

    const secondaryRow = this.querySelector('[data-secondary-row]');
    if (secondaryRow) {
      const hasVisibleSecondary = Array.from(
        secondaryRow.querySelectorAll('[data-parent-value]')
      ).some((tab) => tab.dataset.parentValue === primaryValue);

      secondaryRow.hidden = !primaryValue || !hasVisibleSecondary;
    }

    this.querySelectorAll('[data-parent-value]').forEach((tab) => {
      const parent = tab.dataset.parentValue;
      tab.hidden = Boolean(parent) && parent !== primaryValue;
    });

    this.syncNativeFilterForms(params);
    this.syncSidebarDescription(primaryValue, secondaryValue);
  }

  syncNativeFilterForms(params) {
    document.querySelectorAll('#FacetFiltersForm, #FacetFiltersFormMobile, #FacetSortDrawerForm').forEach((form) => {
      form.querySelectorAll('[data-collection-filter-nav-input]').forEach((input) => input.remove());

      const appendMissingParam = (name, value) => {
        if (!name || !value) return;
        if (Array.from(form.elements).some((control) => control.name === name)) return;

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        input.dataset.collectionFilterNavInput = '';
        form.append(input);
      };

      [this.primaryKey, this.secondaryKey].forEach((key) => {
        appendMissingParam(key, this.getParamValue(key, params));
      });

      appendMissingParam('view', params.get('view'));
    });
  }
}

customElements.define('collection-filter-nav', CollectionFilterNav);
})();
